import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Wand2 } from 'lucide-react';
import { btnFieldAction } from './constants';
import { LockTip } from '../shared/FormKit';
import { fetchJSON } from '../../utils/api';

// "Read the certificate" autofill for External CPD (docs/modules/workflow-ld-cpd.md).
// Shared by the declaration form (Workflow → External CPD Declaration) and HR's
// record modal (L&D Records → External CPD), so both fill in the same way.
//
// Unlike the FS signing-date detector — which runs pdf.js in the browser and
// deliberately does no OCR — a CPD certificate is routinely a scan or a phone
// photo with no text layer, so the file is posted to the server, which reads it
// with a vision model.
//
// ONE FILE CAN YIELD SEVERAL CERTIFICATES: staff scan a few together. The hook
// returns a flat `certificates` list, each entry carrying the `fileIndex` it
// came from, so the caller can build one row per certificate.
//
// Reading starts on its own as soon as readable files are chosen —
// the point is that the form fills itself, not that there is a button to
// press. The button re-runs it.
//
// Returns { busy, button, panel, certificates, rejected }: mount `button`
// beside the file input and `panel` under it. While a read runs the caller
// should disable Submit/Save via `busy`.

const READABLE = /\.(pdf|jpg|jpeg|png)$/i;
const isPdf = (file) => /\.pdf$/i.test(file?.name || '');

/**
 * Can the reader even open this file? (.doc/.docx are legitimate uploads that
 * simply have to be typed in.) Exported so a caller can tell "nothing here is
 * readable, so no read will run" apart from "a read is about to start".
 */
export const isReadableCertificate = (file) => READABLE.test(file?.name || '');

// Mirrors OPENAI_CPD_MAX_PAGES on the server. Checked here too so an oversized
// PDF is rejected the moment it is picked, instead of after an upload — the
// server still enforces it (this side can be bypassed).
export const MAX_CERTIFICATE_PAGES = 10;

const fileKey = (files) =>
  (files || []).map((f) => `${f.name}:${f.size}:${f.lastModified}`).join('|');

// The four facts a certificate has to yield. The declaration form requires ALL
// of them — a certificate missing any one is rejected up
// front rather than sent to review half-filled. HR's modal is not bound by
// this: autofill there is an aid, HR types whatever the record needs.
export const CERTIFICATE_FIELDS = {
  participantName: 'participant name',
  trainingTitle: 'course title',
  certificateDate: 'certificate date',
  hours: 'hours',
};

export const missingOf = (c) => Object.keys(CERTIFICATE_FIELDS).filter((k) => c?.[k] == null);

/** "the hours", "the course title and hours", "the X, Y and Z" */
export const listMissing = (keys) => {
  const names = keys.map((k) => CERTIFICATE_FIELDS[k]);
  if (names.length === 0) return '';
  if (names.length === 1) return `the ${names[0]}`;
  return `the ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/**
 * Page count via pdf.js, same lazy-import + local worker as the FS detector
 * (the worker is served from the site root — see docs/architecture.md §10).
 * Returns null when the file cannot be opened; the caller lets those through
 * for the server to judge rather than blocking on a client-side guess.
 */
const pageCountOf = async (file) => {
  try {
    const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
    pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
    const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
    const pages = doc.numPages;
    await doc.destroy();
    return pages;
  } catch {
    return null;
  }
};

// `selfDeclaration` decides what the name on the certificate means:
//   true  — the declaration form, where the submitter declares their OWN
//           training. A certificate in someone else's name is a warning.
//   false — HR's record modal, where filing on behalf of others is the whole
//           point. The name is a lookup hint, never a warning; telling HR
//           "declare only your own training" on every single record would be
//           a false alarm they learn to ignore.
export const useCpdAutofill = ({
  files, onExtracted, disabled = false, multiple = false, selfDeclaration = true,
}) => {
  const [busy, setBusy] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [fileOutcomes, setFileOutcomes] = useState([]);
  const [rejected, setRejected] = useState([]);   // too many pages — never sent
  const [error, setError] = useState(null);
  const cancelled = useRef(false);
  // Guards against a re-read of the same selection when the parent re-renders.
  const lastRun = useRef('');

  const key = fileKey(files);

  // Re-arm on mount, not just disarm on unmount: StrictMode runs effects
  // mount → unmount → mount in development, so a cleanup-only version leaves
  // the flag stuck at true on the second mount — every result then gets
  // discarded and `busy` never clears (the spinner spins forever).
  useEffect(() => {
    cancelled.current = false;
    return () => { cancelled.current = true; };
  }, []);

  // Keep the tab open while a read is in flight (same guard as the FS detector).
  useEffect(() => {
    if (!busy) return undefined;
    const guard = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [busy]);

  const run = useCallback(async (picked) => {
    const readable = (picked || []).filter((f) => READABLE.test(f.name || ''));
    if (readable.length === 0) return;

    // Belt and braces alongside the mount effect — the same reset the FS
    // detector does at the top of its run.
    cancelled.current = false;
    setBusy(true);
    setCertificates([]);
    setFileOutcomes([]);
    setRejected([]);
    setError(null);

    try {
      // Screen oversized PDFs before uploading anything.
      const tooBig = [];
      const sendable = [];
      for (const f of readable) {
        if (!isPdf(f)) { sendable.push(f); continue; }
        const pages = await pageCountOf(f);
        if (pages !== null && pages > MAX_CERTIFICATE_PAGES) tooBig.push({ name: f.name, pages });
        else sendable.push(f);
      }
      if (cancelled.current) return;
      setRejected(tooBig);

      if (sendable.length === 0) { setBusy(false); return; }

      const form = new FormData();
      const order = multiple ? sendable : sendable.slice(0, 1);
      order.forEach((f) => form.append('files', f));
      const data = await fetchJSON('/api/ld/cpd-extract', { method: 'POST', body: form });
      if (cancelled.current) return;

      // Reader not configured on this server: stay silent and let the user type.
      if (data.enabled === false) return;

      // fileIndex is an index into what was SENT; map it back to the file the
      // caller knows about, since oversized PDFs were dropped along the way.
      const found = (data.certificates || []).map((c) => ({
        ...c,
        file: order[c.fileIndex] || null,
      }));
      setCertificates(found);
      setFileOutcomes((data.files || []).map((f) => ({ ...f, file: order[f.fileIndex] || null })));
      if (found.length > 0) onExtracted(found);
    } catch (e) {
      if (!cancelled.current) setError(e.message);
    } finally {
      if (!cancelled.current) setBusy(false);
    }
  }, [onExtracted, multiple]);

  // Auto-run on a new selection. A selection with nothing readable clears the
  // panel rather than showing "cannot be read" — a Word certificate is a
  // legitimate upload, it just has to be typed in.
  useEffect(() => {
    if (disabled || !key || key === lastRun.current) return;
    lastRun.current = key;
    const readable = (files || []).filter((f) => READABLE.test(f.name || ''));
    if (readable.length === 0) {
      setCertificates([]); setFileOutcomes([]); setRejected([]); setError(null);
      return;
    }
    run(files);
    // `files` is covered by `key`; depending on it directly would re-run on
    // every render because the parent rebuilds the array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, disabled, run]);

  const readableCount = (files || []).filter((f) => READABLE.test(f.name || '')).length;
  const label = multiple && readableCount > 1 ? `Read ${readableCount} Files` : 'Read the Certificate';

  const button = (
    <LockTip text={readableCount > 0 ? null : 'Select a PDF or image certificate to enable reading'}>
      <button
        type="button"
        onClick={() => run(files)}
        disabled={disabled || busy || readableCount === 0}
        className={btnFieldAction}
      >
        {busy
          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Reading…</>
          : <><Wand2 className="w-3.5 h-3.5" />{label}</>}
      </button>
    </LockTip>
  );

  const failedFiles = fileOutcomes.filter((f) => !f.ok && !f.skipped);
  // Grouped by the name printed on the certificate, not listed one per
  // certificate: a PDF holding three of someone else's certificates would
  // print the identical sentence three times, which reads as a glitch rather
  // than as three findings.
  const mismatches = selfDeclaration
    ? Object.values(certificates
      .filter((c) => c.nameMatch && !c.nameMatch.matches)
      .reduce((acc, c) => {
        const key = String(c.participantName || '');
        if (!acc[key]) {
          acc[key] = {
            participantName: c.participantName,
            staffName: c.nameMatch.staffName,
            filenames: [],
            count: 0,
          };
        }
        if (!acc[key].filenames.includes(c.filename)) acc[key].filenames.push(c.filename);
        acc[key].count += 1;
        return acc;
      }, {}))
    : [];
  // Only worth naming the file when the upload has more than one.
  const manyFiles = fileOutcomes.length > 1 || rejected.length > 0;
  // Filing on someone's behalf: who the certificate says it belongs to, and
  // whether that resolved to a staff row.
  const behalfOf = !selfDeclaration && certificates[0]?.participantName
    ? certificates[0]
    : null;
  // …and, once an upload can produce several records at once, whether they all
  // belong to the SAME person. HR picks one staff member for the whole upload,
  // so certificates naming two people would silently file one person's course
  // against the other.
  const namedPeople = selfDeclaration ? [] : [...new Set(
    certificates.map((c) => String(c.participantName || '').trim()).filter(Boolean),
  )];
  const mixedPeople = namedPeople.length > 1;

  const panel = (
    <>
      {busy && (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-center gap-2">
          <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
          <span>
            Reading {readableCount > 1 ? `${readableCount} files` : 'the certificate'} — please keep
            this page open. This takes a few seconds{readableCount > 1 ? ' per file' : ''}.
          </span>
        </div>
      )}

      {!busy && rejected.length > 0 && (
        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          {rejected.map((r) => (
            <p key={r.name}>
              <strong>{r.name}</strong> has {r.pages} pages — certificates are limited
              to {MAX_CERTIFICATE_PAGES}. It was not read. Please upload the certificate on its own.
            </p>
          ))}
        </div>
      )}

      {!busy && certificates.length > 0 && (
        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          <p>
            Found {certificates.length > 1 ? `${certificates.length} certificates` : '1 certificate'}
            {' '}and filled in the details. Please check every field before submitting.
          </p>
          {certificates.length > 1 && (
            <ul className="mt-1 text-xs space-y-0.5">
              {certificates.map((c, i) => {
                const gaps = missingOf(c);
                return (
                  <li key={`${c.filename}-${i}`}>
                    <span className="font-medium">{c.trainingTitle || '(no title read)'}</span>
                    {` — ${c.filename}`}
                    {gaps.length > 0 && ` (could not read ${listMissing(gaps)})`}
                  </li>
                );
              })}
            </ul>
          )}
          {/* What to DO about a gap differs by caller — HR types it in, the
              declaration form refuses the certificate — so only the HR side
              gets advice here; the form states its own rule. */}
          {!selfDeclaration && certificates.length === 1 && missingOf(certificates[0]).length > 0 && (
            <p className="mt-1 text-xs">
              Could not read {listMissing(missingOf(certificates[0]))} — enter
              {missingOf(certificates[0]).length > 1 ? ' them' : ' it'} yourself.
            </p>
          )}
        </div>
      )}

      {/* Filing on someone's behalf: state who the certificate names and
          whether a staff row was found for them. Informational — HR still
          picks the person, this just saves the lookup. */}
      {!busy && behalfOf && !mixedPeople && (
        <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700">
          Issued to “{behalfOf.participantName}”
          {behalfOf.staffSuggestion
            ? <> — selected <strong>{behalfOf.staffSuggestion.name}</strong> as the staff member. Change it if that is not right.</>
            : <> — no matching staff record was found, so please pick the staff member yourself.</>}
        </div>
      )}

      {/* Two names in one upload. Not a block — the reader may simply have
          misread a name — but everything filed here goes to ONE staff member,
          so this has to be said before Save, not discovered afterwards. */}
      {!busy && mixedPeople && (
        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <span>
            These certificates name more than one person ({namedPeople.map((n) => `“${n}”`).join(', ')}).
            Everything filed from this upload goes to the one staff member picked below —
            upload one person’s certificates at a time, or remove the rows that are not theirs.
          </span>
        </div>
      )}

      {/* Name on a certificate belongs to someone else — a warning, never a
          block: certificates legitimately carry maiden names, initials, or the
          provider's own spelling. */}
      {!busy && mismatches.map((m, i) => (
        <div key={`mismatch-${i}`} className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <span>
            {manyFiles && <><strong>{m.filenames.join(', ')}</strong>: </>}
            {m.count > 1
              ? `${m.count} of these certificates are issued to`
              : 'This certificate is issued to'} “{m.participantName}”, which does not look
            like {m.staffName}. Declare only your own training — the reviewer sees this too.
          </span>
        </div>
      ))}

      {!busy && failedFiles.length > 0 && (
        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          {failedFiles.length === 1
            ? <><strong>{failedFiles[0].filename}</strong>: {failedFiles[0].reason || 'could not be read.'} Please fill in the fields yourself.</>
            : <>
                {failedFiles.length} files could not be read
                ({failedFiles.map((f) => f.filename).join(', ')}) — fill those in yourself.
              </>}
        </div>
      )}

      {!busy && error && (
        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          {error} Please fill in the fields yourself.
        </div>
      )}
    </>
  );

  // `files` carries the per-upload outcome (ok / skipped / count / reason) so a
  // caller can explain which upload failed and why; `rejected` is the ones
  // never sent because they exceeded the page limit.
  return { busy, button, panel, certificates, files: fileOutcomes, rejected };
};

export default useCpdAutofill;
